import React, { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { RefreshCw, Wand2, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtGBP = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const fmtAbs = (n) => fmtGBP(Math.abs(n || 0));

export default function Bookkeeping() {
    const [tab, setTab] = useState('reconcile');

    const tabs = [
        { key: 'reconcile',     label: 'Reconciliation' },
        { key: 'needs',         label: 'Needs allocation' },
        { key: 'all',           label: 'All transactions' },
        { key: 'categories',    label: 'Categories' },
        { key: 'rules',         label: 'Rules' },
    ];

    return (
        <div data-testid="bookkeeping-page" className="bm-fade-in">
            <PageHeader title="Bookkeeping" description="Allocate Monzo outgoings to UK SME expense categories. Rules auto-categorise future spend." />
            <div className="px-8 py-6 space-y-5">
                <div className="flex gap-1 border-b border-[color:var(--bm-border)]">
                    {tabs.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition -mb-px ${tab === t.key ? 'border-[color:var(--bm-primary)] text-[color:var(--bm-primary)]' : 'border-transparent text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]'}`}
                            data-testid={`bk-tab-${t.key}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
                {tab === 'reconcile'     && <ReconciliationPanel />}
                {tab === 'needs'         && <DebitsList only="needs" />}
                {tab === 'all'           && <DebitsList only="all" />}
                {tab === 'categories'    && <CategoriesPanel />}
                {tab === 'rules'         && <RulesPanel />}
            </div>
        </div>
    );
}


// ── Debits list with categorise dialog ────────────────────────────────────────

function DebitsList({ only }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [selectedTx, setSelectedTx] = useState(null);
    const [splitTx, setSplitTx] = useState(null);
    const [applying, setApplying] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 500 };
            if (only === 'needs')          params.needs_allocation = true;
            else if (only === 'uncategorised') params.uncategorised_only = true;
            // 'all' returns everything (both credits and debits)
            const r = await api.get('/monzo/transactions', { params });
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setLoading(false); }
    }, [only]);

    useEffect(() => {
        load();
        api.get('/expense-categories').then(r => setCategories(r.data.categories || [])).catch(() => {});
        api.get('/gl/accounts').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
    }, [load]);

    const applyAll = async () => {
        setApplying(true);
        try {
            const r = await api.post('/expense-rules/_apply');
            toast.success(`${r.data.applied} transactions auto-categorised by your rules`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setApplying(false); }
    };

    const uncategorise = async (tx) => {
        try {
            await api.delete(`/monzo/transactions/${tx.id}/categorize`);
            toast.success('Removed allocation');
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };

    const txList = data?.transactions || [];

    return (
        <>
            <div className="flex justify-between items-center">
                <div className="text-[13px] text-[color:var(--bm-text-muted)]">
                    {only === 'needs'
                        ? `${txList.length} transaction${txList.length === 1 ? '' : 's'} awaiting allocation`
                        : `${txList.length} transactions`}
                </div>
                <Button onClick={applyAll} disabled={applying} variant="outline" size="sm" className="h-8" data-testid="bk-apply-rules">
                    <Wand2 size={13} className="mr-1.5" />{applying ? 'Applying…' : 'Apply rules now'}
                </Button>
            </div>

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
                <table className="w-full text-[12.5px]">
                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                        <tr>
                            <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Date</th>
                            <th className="text-left font-medium px-4 py-3">Merchant / Description</th>
                            <th className="text-right font-medium px-3 py-3 whitespace-nowrap">Amount</th>
                            <th className="text-left font-medium px-4 py-3">Allocation</th>
                            <th className="text-left font-medium px-3 py-3 whitespace-nowrap">VAT</th>
                            <th className="text-left font-medium px-3 py-3 whitespace-nowrap">Posted</th>
                            <th className="px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                        {!loading && txList.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-12 text-center text-[color:var(--bm-text-muted)]">
                                {only === 'needs' ? 'Everything is allocated.' : 'No transactions yet — click Sync now in Monzo.'}
                            </td></tr>
                        )}
                        {txList.map(tx => {
                            const merchant = tx.merchant_name || tx.description || '—';
                            const credit = tx.is_credit;
                            return (
                                <tr key={tx.id} className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] transition">
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)] whitespace-nowrap">
                                        {tx.created ? new Date(tx.created).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'2-digit' }) : '—'}
                                    </td>
                                    <td className="px-4 py-3 max-w-[280px]">
                                        <div className="font-medium truncate">{merchant}</div>
                                        {tx.notes && <div className="text-[11px] text-[color:var(--bm-text-subtle)] truncate">{tx.notes}</div>}
                                    </td>
                                    <td className={`px-3 py-3 text-right font-semibold whitespace-nowrap ${credit ? 'text-emerald-600' : ''}`}>
                                        {credit ? '+' : '−'}{fmtAbs(tx.amount_gbp)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {tx.matched_invoice_number ? (
                                            <span className="bm-pill bg-indigo-50 border border-indigo-200 text-indigo-700">
                                                → Invoice {tx.matched_invoice_number}
                                            </span>
                                        ) : tx.splits && tx.splits.length ? (
                                            <div className="space-y-0.5">
                                                <span className="bm-pill bg-violet-50 border border-violet-200 text-violet-700">
                                                    Split × {tx.splits.length}
                                                </span>
                                                <div className="text-[10.5px] text-[color:var(--bm-text-subtle)]">
                                                    {tx.splits.slice(0, 2).map(s => `${s.expense_category_name || s.gl_account_code} £${s.gross}`).join(' · ')}
                                                    {tx.splits.length > 2 ? ` · +${tx.splits.length - 2} more` : ''}
                                                </div>
                                            </div>
                                        ) : tx.expense_category_name ? (
                                            <div className="flex items-center gap-2">
                                                <span className="bm-pill bg-emerald-50 border border-emerald-200 text-emerald-700">{tx.expense_category_name}</span>
                                                {tx.expense_hmrc_sa103_box && tx.expense_hmrc_sa103_box !== '—' && (
                                                    <span className="text-[10.5px] text-[color:var(--bm-text-subtle)]">SA103 box {tx.expense_hmrc_sa103_box}</span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-[color:var(--bm-text-subtle)]">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 text-[color:var(--bm-text-muted)] whitespace-nowrap">
                                        {tx.vat_rate != null ? `${tx.vat_rate}%` : '—'}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        {tx.gl_journal_id
                                            ? <span className="bm-pill bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10.5px]">Posted</span>
                                            : (tx.expense_gl_code || tx.matched_invoice_id || (tx.splits && tx.splits.length))
                                                ? <span className="bm-pill bg-amber-50 border border-amber-200 text-amber-700 text-[10.5px]">Pending</span>
                                                : <span className="text-[color:var(--bm-text-subtle)]">—</span>}
                                    </td>
                                    <td className="px-3 py-3 text-right whitespace-nowrap space-x-3">
                                        {!tx.matched_invoice_id && (
                                            <>
                                                <button onClick={() => setSelectedTx(tx)}
                                                    className="text-[12px] text-[color:var(--bm-primary)] hover:underline"
                                                    data-testid={`bk-allocate-${tx.id}`}>
                                                    {(tx.expense_gl_code || (tx.splits && tx.splits.length)) ? 'Re-allocate' : 'Allocate'}
                                                </button>
                                                <button onClick={() => setSplitTx(tx)}
                                                    className="text-[12px] text-[color:var(--bm-primary)] hover:underline"
                                                    data-testid={`bk-split-${tx.id}`}>
                                                    Split
                                                </button>
                                            </>
                                        )}
                                        {(tx.expense_gl_code || (tx.splits && tx.splits.length)) && (
                                            <button onClick={() => uncategorise(tx)}
                                                className="text-[12px] text-red-600 hover:underline"
                                                data-testid={`bk-uncat-${tx.id}`}>
                                                Clear
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <CategorizeDialog
                tx={selectedTx}
                categories={categories}
                accounts={accounts}
                onClose={() => setSelectedTx(null)}
                onSaved={() => { setSelectedTx(null); load(); }}
            />
            <SplitDialog
                tx={splitTx}
                categories={categories}
                accounts={accounts}
                onClose={() => setSplitTx(null)}
                onSaved={() => { setSplitTx(null); load(); }}
            />
        </>
    );
}


function CategorizeDialog({ tx, categories, accounts, onClose, onSaved }) {
    const [mode, setMode] = useState('category'); // 'category' | 'gl'
    const [catId, setCatId] = useState('');
    const [glCode, setGlCode] = useState('');
    const [vatRate, setVatRate] = useState('');
    const [createRule, setCreateRule] = useState(true);
    const [matchType, setMatchType] = useState('merchant');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!tx) return;
        const isCredit = !!tx.is_credit;
        // Credits default to GL-account mode (e.g. Director's Loan, refund);
        // debits default to category mode (auto-maps to GL via the category).
        setMode(isCredit ? 'gl' : 'category');
        setCatId(tx.expense_category_id || '');
        setGlCode(tx.expense_gl_code || '');
        setVatRate(tx.vat_rate != null ? String(tx.vat_rate) : (isCredit ? '0' : '20'));
        setCreateRule(!isCredit);  // rules are pointless for one-off credits
        setMatchType('merchant');
    }, [tx]);

    if (!tx) return null;
    const isCredit = !!tx.is_credit;
    const merchant = tx.merchant_name || tx.description || '—';

    // Filter accounts to ones that make sense as allocation targets
    const allocAccounts = (accounts || []).filter(a => !a.is_bank && a.code !== '1100' && a.code !== '2100');
    // Group accounts by type for the dropdown
    const grouped = allocAccounts.reduce((m, a) => {
        const t = a.type;
        m[t] = m[t] || []; m[t].push(a);
        return m;
    }, {});
    const groupOrder = ['liability', 'equity', 'income', 'expense', 'asset'];
    const groupLabel = { liability: 'Liabilities', equity: 'Equity / Capital', income: 'Income', expense: 'Expenses (refund)', asset: 'Assets' };

    const submit = async () => {
        if (mode === 'category' && !catId) return toast.error('Pick a category');
        if (mode === 'gl' && !glCode)      return toast.error('Pick an account');
        setSaving(true);
        try {
            const payload = {
                vat_rate: vatRate ? parseFloat(vatRate) : null,
                create_rule: createRule,
                rule_match_type: matchType,
            };
            if (mode === 'category') payload.expense_category_id = catId;
            else                     payload.gl_account_code     = glCode;
            await api.post(`/monzo/transactions/${tx.id}/categorize`, payload);
            toast.success(createRule ? 'Allocated — rule created for future matches' : 'Allocated');
            onSaved();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!tx} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="bk-allocate-dialog">
                <DialogHeader>
                    <DialogTitle>Allocate {isCredit ? 'incoming' : 'expense'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5">
                        <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Transaction</div>
                        <div className="font-medium text-[14px] mt-0.5">{merchant}</div>
                        <div className="text-[12px] text-[color:var(--bm-text-muted)]">
                            {tx.created ? new Date(tx.created).toLocaleDateString('en-GB') : ''} · {isCredit ? '+' : '−'}{fmtAbs(tx.amount_gbp)}
                        </div>
                    </div>

                    {/* Mode toggle — debits can pick either; credits default to direct GL */}
                    <div className="flex gap-1 bg-[color:var(--bm-bg-mute)] p-1 rounded-md">
                        <button
                            onClick={() => setMode('category')}
                            className={`flex-1 py-1.5 px-3 text-[12px] rounded ${mode === 'category' ? 'bg-white shadow-sm font-semibold' : 'text-[color:var(--bm-text-muted)]'}`}>
                            Expense category
                        </button>
                        <button
                            onClick={() => setMode('gl')}
                            className={`flex-1 py-1.5 px-3 text-[12px] rounded ${mode === 'gl' ? 'bg-white shadow-sm font-semibold' : 'text-[color:var(--bm-text-muted)]'}`}>
                            Direct GL account
                        </button>
                    </div>

                    {mode === 'category' && (
                        <div>
                            <Label className="text-[12px]">Category</Label>
                            <Select value={catId} onValueChange={setCatId}>
                                <SelectTrigger className="mt-1.5 h-10" data-testid="bk-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.name}
                                            {c.hmrc_sa103_box && c.hmrc_sa103_box !== '—' && (
                                                <span className="text-[11px] text-[color:var(--bm-text-subtle)] ml-2">· SA103 box {c.hmrc_sa103_box}</span>
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {mode === 'gl' && (
                        <div>
                            <Label className="text-[12px]">GL account</Label>
                            <Select value={glCode} onValueChange={setGlCode}>
                                <SelectTrigger className="mt-1.5 h-10" data-testid="bk-gl-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                                <SelectContent>
                                    {groupOrder.filter(t => grouped[t]).map(t => (
                                        <React.Fragment key={t}>
                                            <div className="px-2 py-1 text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold bg-[color:var(--bm-bg-mute)]">{groupLabel[t]}</div>
                                            {grouped[t].map(a => (
                                                <SelectItem key={a.code} value={a.code}>
                                                    <span className="font-mono text-[11px] mr-2">{a.code}</span> {a.name}
                                                </SelectItem>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1.5">
                                {isCredit
                                    ? 'e.g. Director\'s Loan (2110) for owner top-ups, expense account for refunds, Other Income for misc inflows.'
                                    : 'Direct posting bypasses the category list — useful for one-off allocations.'}
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="text-[12px]">VAT rate (%)</Label>
                        <Input type="number" value={vatRate} onChange={e => setVatRate(e.target.value)} className="mt-1.5 h-10" placeholder="20" data-testid="bk-vat-rate" />
                    </div>

                    {!isCredit && (
                        <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 space-y-2">
                            <label className="flex items-center gap-2 text-[13px] font-medium cursor-pointer">
                                <input type="checkbox" checked={createRule} onChange={e => setCreateRule(e.target.checked)} data-testid="bk-create-rule" />
                                Remember this — auto-categorise future "{matchType === 'merchant' ? merchant : tx.description}"
                            </label>
                            {createRule && mode === 'category' && (
                                <div>
                                    <Label className="text-[11px]">Match by</Label>
                                    <Select value={matchType} onValueChange={setMatchType}>
                                        <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="merchant">Merchant name</SelectItem>
                                            <SelectItem value="description_equals">Description (exact)</SelectItem>
                                            <SelectItem value="description_contains">Description (contains)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            {createRule && mode === 'gl' && (
                                <div className="text-[11px] text-[color:var(--bm-text-subtle)]">Rule will use the linked category — switch to "Expense category" mode to enable rule creation.</div>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: 'var(--bm-primary)' }} data-testid="bk-allocate-save">
                        {saving ? 'Saving…' : 'Allocate'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ── Categories panel ──────────────────────────────────────────────────────────

function CategoriesPanel() {
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showAdd, setShowAdd] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/expense-categories'); setCats(r.data.categories || []); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (cat) => {
        try {
            if (cat.id) {
                const { id, ...rest } = cat;
                await api.patch(`/expense-categories/${id}`, rest);
            } else {
                await api.post('/expense-categories', cat);
            }
            toast.success('Saved');
            setEditing(null); setShowAdd(false); load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Save failed'); }
    };

    const remove = async (id) => {
        if (!confirm('Delete this category? Transactions already allocated to it will keep their allocation.')) return;
        try { await api.delete(`/expense-categories/${id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };

    return (
        <>
            <div className="flex justify-between items-center">
                <div className="text-[13px] text-[color:var(--bm-text-muted)]">{cats.length} categories — UK SME defaults mapped to HMRC SA103 boxes</div>
                <Button onClick={() => setShowAdd(true)} size="sm" className="h-8" style={{ background: 'var(--bm-primary)' }} data-testid="bk-cat-add">+ New category</Button>
            </div>
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
                <table className="w-full text-[12.5px]">
                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                        <tr>
                            <th className="text-left font-medium px-4 py-3">Code</th>
                            <th className="text-left font-medium px-4 py-3">Name</th>
                            <th className="text-left font-medium px-3 py-3">SA103 box</th>
                            <th className="text-left font-medium px-3 py-3">SA103 label</th>
                            <th className="text-right font-medium px-3 py-3 whitespace-nowrap">VAT %</th>
                            <th className="text-left font-medium px-3 py-3">GL account</th>
                            <th className="px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                        {cats.map(c => (
                            <tr key={c.id} className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)]">
                                <td className="px-4 py-3 font-mono text-[11.5px]">{c.code}</td>
                                <td className="px-4 py-3 font-medium">
                                    {c.name}
                                    {c.is_disallowable && <span className="ml-2 text-[10.5px] bm-pill bg-amber-50 border border-amber-200 text-amber-700">Disallowable</span>}
                                    {c.is_capital && <span className="ml-2 text-[10.5px] bm-pill bg-violet-50 border border-violet-200 text-violet-700">Capital</span>}
                                </td>
                                <td className="px-3 py-3 text-[color:var(--bm-text-muted)]">{c.hmrc_sa103_box || '—'}</td>
                                <td className="px-3 py-3 text-[color:var(--bm-text-muted)] max-w-[280px] truncate">{c.hmrc_sa103_label || '—'}</td>
                                <td className="px-3 py-3 text-right text-[color:var(--bm-text-muted)] whitespace-nowrap">{c.default_vat_rate != null ? `${c.default_vat_rate}%` : '—'}</td>
                                <td className="px-3 py-3 font-mono text-[11.5px] text-[color:var(--bm-text-muted)]">{c.gl_account_code || '—'}</td>
                                <td className="px-3 py-3 text-right whitespace-nowrap space-x-3">
                                    <button onClick={() => setEditing(c)} className="text-[12px] text-[color:var(--bm-primary)] hover:underline">Edit</button>
                                    <button onClick={() => remove(c.id)} className="text-[12px] text-red-600 hover:underline">Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {(editing || showAdd) && (
                <CategoryEditor
                    category={editing}
                    onClose={() => { setEditing(null); setShowAdd(false); }}
                    onSave={save}
                />
            )}
        </>
    );
}

function CategoryEditor({ category, onClose, onSave }) {
    const [form, setForm] = useState(category || {
        code: '', name: '', hmrc_sa103_box: '', hmrc_sa103_label: '',
        default_vat_rate: 20, gl_account_code: '', is_disallowable: false, is_capital: false,
    });
    const u = (k, v) => setForm(s => ({ ...s, [k]: v }));

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{category ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="text-[12px]">Code</Label>
                        <Input value={form.code || ''} onChange={e => u('code', e.target.value)} className="mt-1.5 h-10" />
                    </div>
                    <div>
                        <Label className="text-[12px]">Name</Label>
                        <Input value={form.name || ''} onChange={e => u('name', e.target.value)} className="mt-1.5 h-10" />
                    </div>
                    <div>
                        <Label className="text-[12px]">HMRC SA103 box</Label>
                        <Input value={form.hmrc_sa103_box || ''} onChange={e => u('hmrc_sa103_box', e.target.value)} className="mt-1.5 h-10" placeholder="e.g. 27" />
                    </div>
                    <div>
                        <Label className="text-[12px]">Default VAT %</Label>
                        <Input type="number" value={form.default_vat_rate ?? ''} onChange={e => u('default_vat_rate', parseFloat(e.target.value) || 0)} className="mt-1.5 h-10" />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-[12px]">HMRC SA103 label / description</Label>
                        <Input value={form.hmrc_sa103_label || ''} onChange={e => u('hmrc_sa103_label', e.target.value)} className="mt-1.5 h-10" />
                    </div>
                    <div>
                        <Label className="text-[12px]">GL account code</Label>
                        <Input value={form.gl_account_code || ''} onChange={e => u('gl_account_code', e.target.value)} className="mt-1.5 h-10" placeholder="e.g. 7300" />
                    </div>
                    <div className="flex items-end gap-4">
                        <label className="flex items-center gap-1.5 text-[12.5px]">
                            <input type="checkbox" checked={!!form.is_disallowable} onChange={e => u('is_disallowable', e.target.checked)} />
                            Disallowable
                        </label>
                        <label className="flex items-center gap-1.5 text-[12.5px]">
                            <input type="checkbox" checked={!!form.is_capital} onChange={e => u('is_capital', e.target.checked)} />
                            Capital
                        </label>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => onSave(form)} style={{ background: 'var(--bm-primary)' }}>Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ── Rules panel ───────────────────────────────────────────────────────────────

function RulesPanel() {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/expense-rules'); setRules(r.data.rules || []); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        if (!confirm('Delete this rule? Future matching transactions will not be auto-categorised.')) return;
        try { await api.delete(`/expense-rules/${id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
            <table className="w-full text-[12.5px]">
                <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                    <tr>
                        <th className="text-left font-medium px-4 py-3">Match</th>
                        <th className="text-left font-medium px-4 py-3">Match value</th>
                        <th className="text-left font-medium px-4 py-3">Category</th>
                        <th className="text-right font-medium px-3 py-3 whitespace-nowrap">VAT</th>
                        <th className="text-right font-medium px-3 py-3 whitespace-nowrap">Hits</th>
                        <th className="text-left font-medium px-3 py-3 whitespace-nowrap">Last applied</th>
                        <th className="px-3 py-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                    {!loading && rules.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-[color:var(--bm-text-muted)]">
                            No rules yet — create one by allocating a debit and ticking "Remember this".
                        </td></tr>
                    )}
                    {rules.map(r => (
                        <tr key={r.id} className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)]">
                            <td className="px-4 py-3 capitalize text-[color:var(--bm-text-muted)]">{(r.match_type || '').replace('_', ' ')}</td>
                            <td className="px-4 py-3 font-mono text-[11.5px]">{r.match_value}</td>
                            <td className="px-4 py-3 font-medium">{r.expense_category_name || '—'}</td>
                            <td className="px-3 py-3 text-right text-[color:var(--bm-text-muted)] whitespace-nowrap">{r.vat_rate != null ? `${r.vat_rate}%` : '—'}</td>
                            <td className="px-3 py-3 text-right whitespace-nowrap">{r.hits || 0}</td>
                            <td className="px-3 py-3 text-[color:var(--bm-text-muted)] whitespace-nowrap">{r.last_applied_at ? new Date(r.last_applied_at).toLocaleDateString('en-GB') : '—'}</td>
                            <td className="px-3 py-3 text-right">
                                <button onClick={() => remove(r.id)} className="text-[12px] text-red-600 hover:underline">Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}



// ── Split transaction dialog ──────────────────────────────────────────────────

function SplitDialog({ tx, categories, accounts, onClose, onSaved }) {
    const [splits, setSplits] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!tx) return;
        // Hydrate from existing splits if present, otherwise start with two halves
        if (tx.splits && tx.splits.length) {
            setSplits(tx.splits.map(s => ({
                amount: String(s.gross || 0),
                expense_category_id: s.expense_category_id || '',
                gl_account_code:    s.gl_account_code || '',
                vat_rate:           s.vat_rate != null ? String(s.vat_rate) : '20',
                description:        s.description || '',
                mode:               s.expense_category_id ? 'category' : 'gl',
            })));
        } else {
            const gross = Math.abs(tx.amount_gbp || 0);
            const half = (gross / 2).toFixed(2);
            setSplits([
                { amount: half, expense_category_id: '', gl_account_code: '', vat_rate: '20', description: '', mode: 'category' },
                { amount: half, expense_category_id: '', gl_account_code: '', vat_rate: '20', description: '', mode: 'category' },
            ]);
        }
    }, [tx]);

    if (!tx) return null;
    const gross = Math.abs(tx.amount_gbp || 0);
    const merchant = tx.merchant_name || tx.description || '—';

    const sum = splits.reduce((t, s) => t + (parseFloat(s.amount) || 0), 0);
    const balanced = Math.abs(sum - gross) < 0.01 && splits.every(s => (parseFloat(s.amount) || 0) > 0);

    const upd = (idx, field, value) => setSplits(arr => arr.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    const add = () => setSplits(arr => [...arr, { amount: '', expense_category_id: '', gl_account_code: '', vat_rate: '20', description: '', mode: 'category' }]);
    const del = (idx) => setSplits(arr => arr.length > 2 ? arr.filter((_, i) => i !== idx) : arr);
    const fillRemainder = (idx) => {
        const others = splits.reduce((t, s, i) => i === idx ? t : t + (parseFloat(s.amount) || 0), 0);
        upd(idx, 'amount', Math.max(0, gross - others).toFixed(2));
    };

    const allocAccounts = (accounts || []).filter(a => !a.is_bank && a.code !== '1100' && a.code !== '2100');
    const grouped = allocAccounts.reduce((m, a) => { (m[a.type] = m[a.type] || []).push(a); return m; }, {});
    const groupOrder = ['expense', 'liability', 'equity', 'income', 'asset'];
    const groupLabel = { liability: 'Liabilities', equity: 'Equity / Capital', income: 'Income', expense: 'Expenses', asset: 'Assets' };

    const submit = async () => {
        if (!balanced) return toast.error(`Splits must total £${gross.toFixed(2)}`);
        for (const s of splits) {
            if (s.mode === 'category' && !s.expense_category_id) return toast.error('Pick a category for every line');
            if (s.mode === 'gl'       && !s.gl_account_code)     return toast.error('Pick a GL account for every line');
        }
        setSaving(true);
        try {
            await api.post(`/monzo/transactions/${tx.id}/split`, {
                splits: splits.map(s => ({
                    amount: parseFloat(s.amount),
                    expense_category_id: s.mode === 'category' ? s.expense_category_id : null,
                    gl_account_code:     s.mode === 'gl'       ? s.gl_account_code     : null,
                    vat_rate:            s.vat_rate ? parseFloat(s.vat_rate) : null,
                    description:         s.description || null,
                })),
            });
            toast.success('Transaction split & posted');
            onSaved();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!tx} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="bk-split-dialog">
                <DialogHeader>
                    <DialogTitle>Split transaction</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5 flex items-center justify-between">
                        <div>
                            <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Transaction</div>
                            <div className="font-medium text-[14px] mt-0.5">{merchant}</div>
                            <div className="text-[12px] text-[color:var(--bm-text-muted)]">{tx.created ? new Date(tx.created).toLocaleDateString('en-GB') : ''}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Gross</div>
                            <div className="font-display text-[22px] font-semibold">{fmtGBP(gross)}</div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-[color:var(--bm-border)] divide-y divide-[color:var(--bm-border)]">
                        {splits.map((s, idx) => (
                            <div key={idx} className="p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-[11.5px] uppercase tracking-wide font-semibold text-[color:var(--bm-text-subtle)]">Line {idx + 1}</div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => fillRemainder(idx)} className="text-[11px] text-[color:var(--bm-primary)] hover:underline">Fill remainder</button>
                                        <button onClick={() => del(idx)} disabled={splits.length <= 2} className="text-red-600 hover:text-red-700 disabled:opacity-30">
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex gap-1 bg-[color:var(--bm-bg-mute)] p-0.5 rounded text-[11px]">
                                    <button onClick={() => upd(idx, 'mode', 'category')}
                                        className={`flex-1 py-1 rounded ${s.mode === 'category' ? 'bg-white shadow-sm font-semibold' : 'text-[color:var(--bm-text-muted)]'}`}>Category</button>
                                    <button onClick={() => upd(idx, 'mode', 'gl')}
                                        className={`flex-1 py-1 rounded ${s.mode === 'gl' ? 'bg-white shadow-sm font-semibold' : 'text-[color:var(--bm-text-muted)]'}`}>GL account</button>
                                </div>
                                <div className="grid grid-cols-12 gap-2">
                                    <div className="col-span-5">
                                        {s.mode === 'category' ? (
                                            <Select value={s.expense_category_id} onValueChange={v => upd(idx, 'expense_category_id', v)}>
                                                <SelectTrigger className="h-9"><SelectValue placeholder="Select category" /></SelectTrigger>
                                                <SelectContent>
                                                    {categories.map(c => (
                                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Select value={s.gl_account_code} onValueChange={v => upd(idx, 'gl_account_code', v)}>
                                                <SelectTrigger className="h-9"><SelectValue placeholder="Pick account" /></SelectTrigger>
                                                <SelectContent>
                                                    {groupOrder.filter(t => grouped[t]).map(t => (
                                                        <React.Fragment key={t}>
                                                            <div className="px-2 py-1 text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold bg-[color:var(--bm-bg-mute)]">{groupLabel[t]}</div>
                                                            {grouped[t].map(a => (
                                                                <SelectItem key={a.code} value={a.code}>
                                                                    <span className="font-mono text-[11px] mr-2">{a.code}</span> {a.name}
                                                                </SelectItem>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                    <div className="col-span-3">
                                        <Input value={s.description} onChange={e => upd(idx, 'description', e.target.value)} placeholder="Description (optional)" className="h-9" />
                                    </div>
                                    <div className="col-span-2">
                                        <Input type="number" step="0.01" value={s.amount} onChange={e => upd(idx, 'amount', e.target.value)} placeholder="Amount" className="h-9 text-right" />
                                    </div>
                                    <div className="col-span-2">
                                        <Input type="number" value={s.vat_rate} onChange={e => upd(idx, 'vat_rate', e.target.value)} placeholder="VAT %" className="h-9 text-right" />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="px-3 py-2">
                            <button onClick={add} className="text-[12px] text-[color:var(--bm-primary)] hover:underline">+ Add line</button>
                        </div>
                    </div>

                    <div className={`rounded-lg p-3 text-[13px] flex justify-between font-medium ${balanced ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                        <div>Splits total: {fmtGBP(sum)}</div>
                        <div>Transaction gross: {fmtGBP(gross)}</div>
                        <div>{balanced ? '✓ Balanced' : `Difference ${fmtGBP(gross - sum)}`}</div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !balanced} style={{ background: 'var(--bm-primary)' }} data-testid="bk-split-save">
                        {saving ? 'Posting…' : 'Save split'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ── Reconciliation panel ──────────────────────────────────────────────────────

function ReconciliationPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [glEnabled, setGlEnabled] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [recon, settings] = await Promise.all([
                api.get('/gl/bank-reconciliation'),
                api.get('/settings'),
            ]);
            setData(recon.data);
            setGlEnabled(!!settings.data.gl_enabled);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const postUnposted = async () => {
        if (!confirm('Post all unposted allocations and matched payments to the General Ledger?')) return;
        setPosting(true);
        try {
            const r = await api.post('/gl/post-unposted');
            toast.success(`Posted ${r.data.expenses} expenses and ${r.data.payments} payments` + (r.data.errors ? ` (${r.data.errors} errors)` : ''));
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
        } finally { setPosting(false); }
    };

    if (loading || !data) return <div className="text-[color:var(--bm-text-muted)]">Loading reconciliation…</div>;

    const totalUnposted = (data.unposted.categorised_unposted || 0) + (data.unposted.matched_credits_unposted || 0);
    const diff = data.difference;
    const inBalance = data.in_balance;
    return (
        <div className="space-y-5" data-testid="bk-reconcile-panel">
            {!glEnabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-[13px] text-amber-800">
                    General Ledger is disabled in <a href="/settings" className="underline font-semibold">Settings</a>. Enable it to start posting journals.
                </div>
            )}

            {/* Headline reconciliation cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                    <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">GL bank balance</div>
                    <div className="font-display text-[26px] font-semibold mt-1">{fmtGBP(data.book_balance)}</div>
                    <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">
                        Sum of {(data.bank_accounts || []).map(a => a.code).join(', ') || '1200'} on the General Ledger
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                    <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Monzo balance</div>
                    <div className="font-display text-[26px] font-semibold mt-1">{data.monzo_balance != null ? fmtGBP(data.monzo_balance) : '—'}</div>
                    <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">
                        {data.monzo_total_balance != null && data.monzo_total_balance !== data.monzo_balance && (
                            <>Total incl. pots {fmtGBP(data.monzo_total_balance)} · </>
                        )}
                        {data.monzo_balance_as_of
                            ? `As at ${new Date(data.monzo_balance_as_of).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'2-digit', hour:'2-digit', minute:'2-digit' })}`
                            : 'Sync transactions to fetch the latest balance'}
                    </div>
                </div>
                <div className={`rounded-xl p-5 border-2 ${inBalance ? 'bg-emerald-50 border-emerald-300' : (diff != null && Math.abs(diff) > 0.01 ? 'bg-red-50 border-red-300' : 'bg-[color:var(--bm-bg-mute)] border-[color:var(--bm-border)]')}`}>
                    <div className="text-[11px] uppercase tracking-wide font-semibold">{inBalance ? '✓ Reconciled' : 'Difference'}</div>
                    <div className="font-display text-[26px] font-semibold mt-1">{diff != null ? fmtGBP(diff) : '—'}</div>
                    <div className="text-[11px] mt-1">
                        {inBalance
                            ? 'Books match Monzo to the penny'
                            : (diff != null
                                ? `GL ${diff > 0 ? 'exceeds' : 'is below'} Monzo by ${fmtGBP(Math.abs(diff))}`
                                : 'No Monzo balance to compare against yet')}
                    </div>
                </div>
            </div>

            {/* Unposted breakdown */}
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)]">
                <div className="px-5 py-3 border-b border-[color:var(--bm-border)] flex items-center justify-between">
                    <div>
                        <div className="font-display text-[14px] font-semibold">Unposted activity</div>
                        <div className="text-[11.5px] text-[color:var(--bm-text-muted)]">
                            Items below have not yet hit the General Ledger
                        </div>
                    </div>
                    {totalUnposted > 0 && glEnabled && (
                        <Button onClick={postUnposted} disabled={posting} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="bk-post-unposted">
                            {posting ? 'Posting…' : `Post ${totalUnposted} unposted`}
                        </Button>
                    )}
                </div>
                <div className="divide-y divide-[color:var(--bm-border)]">
                    <ReconRow label="Categorised debits awaiting GL post"
                        value={data.unposted.categorised_unposted}
                        hint="Run 'Post unposted' to journal these"
                        kind={data.unposted.categorised_unposted > 0 ? 'warning' : 'ok'} />
                    <ReconRow label="Matched credits without payment record"
                        value={data.unposted.matched_credits_unposted}
                        hint="Auto-reconciled to invoices but not yet posted"
                        kind={data.unposted.matched_credits_unposted > 0 ? 'warning' : 'ok'} />
                    <ReconRow label="Uncategorised debits"
                        value={data.unposted.uncategorised_debits}
                        hint="Allocate them in the Needs allocation tab"
                        kind={data.unposted.uncategorised_debits > 0 ? 'info' : 'ok'} />
                    <ReconRow label="Unallocated credits (not invoice payments)"
                        value={data.unposted.unallocated_credits || 0}
                        hint="e.g. director loans, refunds, capital injections — allocate in the Needs allocation tab"
                        kind={(data.unposted.unallocated_credits || 0) > 0 ? 'info' : 'ok'} />
                    <ReconRow label="Unmatched credits"
                        value={data.unposted.unmatched_credits}
                        hint="Match to invoices in the Monzo page, or allocate as a non-invoice credit"
                        kind={data.unposted.unmatched_credits > 0 ? 'info' : 'ok'} />
                </div>
            </div>
        </div>
    );
}

function ReconRow({ label, value, hint, kind }) {
    const colors = {
        ok:      'text-emerald-700',
        warning: 'text-amber-700',
        info:    'text-indigo-700',
    };
    return (
        <div className="px-5 py-3 flex items-center justify-between">
            <div>
                <div className="text-[13.5px] font-medium">{label}</div>
                <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">{hint}</div>
            </div>
            <div className={`font-display text-[20px] font-semibold ${colors[kind]}`}>{value}</div>
        </div>
    );
}
